import { InputComponent, XRInputManager } from "@iwsdk/xr-input";
import * as THREE from "three";

/**
 * XR input, on Meta's own implementation rather than ours.
 *
 * This replaces a hand-rolled input layer that had three real bugs in it, all of
 * which this package makes structurally impossible:
 *
 *   - **Guessed button indices.** We assumed A/X sat at gamepad index 4. IWSDK
 *     resolves buttons from the controller's actual WebXR input profile and
 *     names them (`A_Button`, `X_Button`, `Trigger`), so there is nothing to
 *     guess and nothing to get wrong per-device.
 *   - **Mismatched pose pairing.** We read button state from
 *     `session.inputSources` but positions from three's `getController(i)`, and
 *     those orderings are not guaranteed to agree — the left hand's buttons
 *     could pair with the right hand's position. `XROrigin` exposes per-hand
 *     grip and ray spaces directly.
 *   - **Manual edge latches.** Every toggle needed its own "was it pressed last
 *     frame" bookkeeping. `getButtonDown` is a real rising edge.
 *
 * It also draws proper animated controller models, which we never had.
 */

export type Hand = "left" | "right";

export interface HandState {
  readonly position: THREE.Vector3;
  readonly quaternion: THREE.Quaternion;
  /** Holding the world: either face button, or squeeze. */
  gripping: boolean;
  /** Analogue trigger, used for pointing/selecting and vertical flight. */
  trigger: number;
  /** Thumbstick, already dead-zoned by IWSDK. */
  readonly stick: { x: number; y: number };
  tracked: boolean;
}

function emptyHand(): HandState {
  return {
    position: new THREE.Vector3(),
    quaternion: new THREE.Quaternion(),
    gripping: false,
    trigger: 0,
    stick: { x: 0, y: 0 },
    tracked: false
  };
}

export class XrInput {
  readonly manager: XRInputManager;
  readonly left = emptyHand();
  readonly right = emptyHand();

  constructor(camera: THREE.PerspectiveCamera, scene: THREE.Scene) {
    this.manager = new XRInputManager({ camera, scene });
  }

  /** The rig. Locomotion moves this; the headset pose lives inside it. */
  get origin(): THREE.Group {
    return this.manager.xrOrigin;
  }

  /**
   * The head, as maintained by IWSDK from the XRViewerPose.
   *
   * Worth using in preference to the three camera: in an immersive session three
   * never writes the camera's position/quaternion, so the ordinary getters hand
   * back the rig's transform instead of the head's.
   */
  get head(): THREE.Group {
    return this.manager.xrOrigin.head;
  }

  update(xr: THREE.WebXRManager, delta: number, time: number): void {
    this.manager.update(xr, delta, time);

    for (const hand of ["left", "right"] as const) {
      const state = hand === "left" ? this.left : this.right;
      const pad = this.manager.gamepads[hand];
      const grip = this.manager.xrOrigin.gripSpaces[hand];

      state.tracked = pad !== undefined;
      if (pad === undefined) {
        state.gripping = false;
        state.trigger = 0;
        state.stick.x = 0;
        state.stick.y = 0;
        continue;
      }

      grip.updateWorldMatrix(true, false);
      state.position.setFromMatrixPosition(grip.matrixWorld);
      grip.getWorldQuaternion(state.quaternion);

      const face =
        hand === "left" ? InputComponent.X_Button : InputComponent.A_Button;
      const alt =
        hand === "left" ? InputComponent.Y_Button : InputComponent.B_Button;
      // Either face button or squeeze counts, so a missed press on one hand
      // cannot silently degrade a two-handed gesture into a one-handed one.
      state.gripping =
        pad.getButtonPressed(face) ||
        pad.getButtonPressed(alt) ||
        pad.getButtonPressed(InputComponent.Squeeze);
      state.trigger = pad.getButtonValue(InputComponent.Trigger);

      const stick = pad.getAxesValues(InputComponent.Thumbstick);
      state.stick.x = stick?.x ?? 0;
      state.stick.y = stick?.y ?? 0;
    }
  }

  /** True on the frame the menu button is first pressed, either hand. */
  menuPressed(): boolean {
    for (const hand of ["left", "right"] as const) {
      const pad = this.manager.gamepads[hand];
      if (pad === undefined) {
        continue;
      }
      const secondary =
        hand === "left" ? InputComponent.Y_Button : InputComponent.B_Button;
      if (pad.getButtonDown(secondary) || pad.getButtonDown(InputComponent.Menu)) {
        return true;
      }
    }
    return false;
  }

  /** True on the frame the right trigger is first squeezed. */
  selectPressed(): boolean {
    const pad = this.manager.gamepads.right;
    return pad?.getButtonDown(InputComponent.Trigger) ?? false;
  }
}

/**
 * Asks the session for 72 Hz.
 *
 * Quest Browser can hand a WebXR session 90 Hz, which sounds better and is
 * actively worse here: it cuts the frame budget from 13.9 ms to 11.1 ms, and
 * Probe 1 measured this app as fragment-bound, so the extra 25% of budget buys
 * more than the extra frames do. Meta's own gate is 72 FPS.
 */
export function requestComfortableFrameRate(session: XRSession): void {
  const rates = session.supportedFrameRates;
  if (rates === undefined || session.updateTargetFrameRate === undefined) {
    return;
  }
  const target = Array.from(rates).includes(72) ? 72 : undefined;
  if (target !== undefined && session.frameRate !== target) {
    void session.updateTargetFrameRate(target).catch(() => {
      /* Not fatal — the session keeps whatever rate it had. */
    });
  }
}
